// Lushin Discovery Call Rubric dimensions — the 10 categories the post-call
// analyzer scores (1-5, normalized to 0-100 in the rollup). Single source of
// truth for the portal's dimension columns/charts. Keep in sync with
// backend-tenant/src/config/rubricDimensions.js and the rollup-service copy.

export type RubricDimensionKey =
  | 'up_front_contract_posture'
  | 'opening_question_vs_story'
  | 'pain_discovery_levels'
  | 'pain_with_price_tag'
  | 'things_prospect_volunteered'
  | 'negative_reverse_reversing'
  | 'equal_business_stature'
  | 'budget_decision_process'
  | 'up_front_contract_close'
  | 'coachs_discipline';

export interface RubricDimension {
  key: RubricDimensionKey;
  label: string;
  /**
   * Abbreviated label for TABLE headers, where one column is ~90px and cannot
   * wrap without wrecking row height.
   */
  short: string;
  /**
   * Label for the chart x-axis. Longer than `short` on purpose: the axis
   * staggers labels across two rows and wraps them, so it has roughly twice the
   * horizontal room a table header does. "EBS" and "Pain $" saved space nobody
   * needed there and cost the reader the meaning.
   */
  chartLabel: string;
}

export const RUBRIC_DIMENSIONS: RubricDimension[] = [
  {
    key: 'up_front_contract_posture',
    label: 'Up-Front Contract & Posture',
    short: 'UFC/Posture',
    chartLabel: 'Up-Front Contract & Posture'
  },
  {
    key: 'opening_question_vs_story',
    label: 'Opening — Question vs. Story',
    short: 'Opening',
    chartLabel: 'Opening — Question vs. Story'
  },
  {
    key: 'pain_discovery_levels',
    label: 'Pain Discovery',
    short: 'Pain',
    chartLabel: 'Pain Discovery'
  },
  {
    key: 'pain_with_price_tag',
    label: 'Pain With a Price Tag',
    short: 'Pain $',
    chartLabel: 'Pain $ Tag'
  },
  {
    key: 'things_prospect_volunteered',
    label: 'Things the Prospect Volunteered',
    short: 'Volunteered',
    chartLabel: 'Prospect Volunteered'
  },
  {
    key: 'negative_reverse_reversing',
    label: 'Negative Reverse / Reversing',
    short: 'Neg. Reverse',
    chartLabel: 'Negative Reverse'
  },
  {
    key: 'equal_business_stature',
    label: 'Equal Business Stature',
    short: 'EBS',
    chartLabel: 'Equal Business Stature'
  },
  {
    key: 'budget_decision_process',
    label: 'Budget & Decision Process',
    short: 'Budget/Decision',
    chartLabel: 'Budget & Decision Process'
  },
  {
    key: 'up_front_contract_close',
    label: 'Up-Front Contract for the Close',
    short: 'Close UFC',
    chartLabel: 'Up-Front Contract (Close)'
  },
  {
    key: 'coachs_discipline',
    label: "Coach's Discipline",
    short: 'Discipline',
    chartLabel: "Coach's Discipline"
  }
];

export const RUBRIC_DIMENSION_KEYS: RubricDimensionKey[] = RUBRIC_DIMENSIONS.map((d) => d.key);

/**
 * The five dimensions worth putting in a cross-client table.
 *
 * All ten are scored and all ten are stored — this is a REPORTING choice, not a
 * grading one. Ten columns forced every header to truncate ("VOLUNTEE…",
 * "BUDGET/D…") and turned the table into a wall of numbers with no hierarchy,
 * so nothing stood out and the important columns were no easier to find than
 * the rest.
 *
 * These five are the four tier-1 categories — the "at any cost" set that the
 * live checklist prioritises and the post-call weight editor protects — plus
 * Things the Prospect Volunteered, which is where a call is won or lost even
 * though it is not tier-1.
 *
 * The other five stay visible on the rep drill-down and in the rubric itself;
 * they are simply not what a super-admin scans a client list for.
 */
export const PRIMARY_RUBRIC_DIMENSION_KEYS: RubricDimensionKey[] = [
  'up_front_contract_posture', // Cat 1 — tier 1
  'pain_discovery_levels', // Cat 3 — tier 1
  'pain_with_price_tag', // Cat 4 — tier 1
  'things_prospect_volunteered', // Cat 5
  'up_front_contract_close' // Cat 9 — tier 1
];

export const PRIMARY_RUBRIC_DIMENSIONS: RubricDimension[] = PRIMARY_RUBRIC_DIMENSION_KEYS.map(
  (key) => RUBRIC_DIMENSIONS.find((d) => d.key === key) as RubricDimension
);

// A bag of per-dimension scores (0-100, null when unscored), keyed by rubric key.
export type RubricScores = { [K in RubricDimensionKey]: number | null };

// Convert a normalized 0-100 score back to the rubric's native 1-5 scale.
export const toFivePoint = (score: number | null): number | null =>
  score == null ? null : Math.round((score / 20) * 10) / 10;
